// topicDeriver.js — SHARED TYPE-FILTER ("topic" axis) deriver for the Community Hub.
//
// This implements the SHARED DERIVER CONTRACT that must be IDENTICAL on every
// surface (web, iOS, the publish + backfill derivers). See
// mchatai-source/frameworks/_taxonomy.json `_purpose` for the canonical spec.
//
// deriveTopics(item, taxonomy, bundleHtml) -> [pillId] (in taxonomy pill order, unique)
//   hay  = lowercase(tags.join(" "))   // curated tags ONLY
//   cc   = lowercase(contentCategory or "")
//   html = bundleHtml or ""   // empty for read-time fallback; present for publish + backfill
//   for each pill in taxonomy.pills:
//      matched = false
//      if cc != "" AND cc is in (pill.contentCategory lowercased)            -> matched = true
//      if html != "" AND any(fp substring-in html for fp in pill.fingerprints) -> matched = true
//      if any(alias token/phrase match in hay for alias in pill.aliases)     -> matched = true
//      if matched: add pill.id to result
//   return result
//
// Tightened 2026-06-23 (Hub topic accuracy): the alias haystack is the
// publisher's CURATED TAGS only — name/description/summary are intentionally
// EXCLUDED. Free-text prose matching mis-filed items (a "Blackjack" card game
// surfaced under "Data Viz"); tags are the explicit signal. contentCategory +
// bundle fingerprints stay authoritative. This must remain byte-for-byte
// equivalent to the iOS + backfill derivers.
//
// effectiveTopics(item, taxonomy) derives from current metadata instead of
// trusting old stored topics, because older backfills can carry stale aliases.

const lc = (v) => (typeof v === 'string' ? v : (v == null ? '' : String(v))).toLowerCase();

/**
 * Derive the set of topic pill ids for a hub item per the shared contract.
 * @param {object} item - hub item with name/description/tags/contentCategory.
 * @param {object} taxonomy - the taxonomy.json object (has .pills).
 * @param {string} [bundleHtml] - the bundle index.html; "" for read-time fallback.
 * @returns {string[]} pill ids in taxonomy pill order, unique.
 */
export function deriveTopics(item, taxonomy, bundleHtml = '') {
  const it = item || {};
  const tax = taxonomy || {};
  const pills = Array.isArray(tax.pills) ? tax.pills : [];

  // Curated tags ONLY — name/description/summary are excluded from alias
  // matching on purpose (see the contract note above).
  const tags = Array.isArray(it.tags) ? it.tags : [];
  const hay = lc(tags.join(' '));
  const cc = lc(it.contentCategory || it.type || '');
  const html = bundleHtml || '';

  const result = [];
  for (const pill of pills) {
    if (!pill || !pill.id) continue;
    let matched = false;

    const pillCategories = (Array.isArray(pill.contentCategory) ? pill.contentCategory : []).map(lc);
    if (cc !== '' && pillCategories.includes(cc)) {
      matched = true;
    }

    if (!matched && html !== '') {
      const fps = Array.isArray(pill.fingerprints) ? pill.fingerprints : [];
      if (fps.some((fp) => fp && html.includes(fp))) {
        matched = true;
      }
    }

    if (!matched) {
      const aliases = Array.isArray(pill.aliases) ? pill.aliases : [];
      if (aliases.some((alias) => aliasMatches(alias, hay))) {
        matched = true;
      }
    }

    if (matched) result.push(pill.id);
  }
  return result;
}

/**
 * Effective topics for filtering: current read-time metadata derivation.
 * @param {object} item - hub item (may carry a stored `topics` array).
 * @param {object} taxonomy - the taxonomy.json object.
 * @returns {string[]} pill ids.
 */
export function effectiveTopics(item, taxonomy) {
  return deriveTopics(item, taxonomy, '');
}

function aliasMatches(alias, hay) {
  const needle = lc(alias).trim();
  if (!needle) return false;

  let start = 0;
  while (start < hay.length) {
    const index = hay.indexOf(needle, start);
    if (index < 0) return false;
    const before = index === 0 ? '' : hay[index - 1];
    const afterIndex = index + needle.length;
    const after = afterIndex >= hay.length ? '' : hay[afterIndex];
    if (!isAliasWordCharacter(before) && !isAliasWordCharacter(after)) {
      return true;
    }
    start = index + needle.length;
  }
  return false;
}

function isAliasWordCharacter(character) {
  return /[a-z0-9]/i.test(character);
}
